import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Eye, EyeOff, Check, X, AlertTriangle, Trash2, Sun, Moon, QrCode, Smartphone, Trash } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import ReadOnlyBanner from './ReadOnlyBanner';

const PROVIDERS = [
  { key: 'openai', label: 'OpenAI (GPT-4o)', placeholder: 'sk-...' },
  { key: 'anthropic', label: 'Anthropic (Claude)', placeholder: 'sk-ant-...' },
  { key: 'gemini', label: 'Google (Gemini)', placeholder: 'AIza...' },
];

function ProviderBlock({ provider, settings, onSaved }) {
  const [show, setShow] = useState(false);
  const [value, setValue] = useState('');
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null); // null | 'ok' | 'fail'
  const [saving, setSaving] = useState(false);

  const isSet = settings?.[`${provider.key}_key_set`];

  const handleTest = async () => {
    setTesting(true);
    setStatus(null);
    try {
      const res = await api.post('/routeops/ai/test-key', {
        provider: provider.key,
        key: value,
      });
      setStatus(res.data.ok ? 'ok' : 'fail');
      if (res.data.ok) toast.success(`${provider.label} key validated`);
      else toast.error(`Validation failed: ${res.data.error || res.data.body || 'invalid key'}`);
    } catch (e) {
      setStatus('fail');
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!value) return toast.error('Enter a key first');
    setSaving(true);
    try {
      await api.put('/routeops/settings', { [`${provider.key}_key`]: value });
      toast.success(`${provider.label} key saved`);
      setValue('');
      setShow(false);
      onSaved();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${provider.label} key?`)) return;
    try {
      await api.put('/routeops/settings', { [`${provider.key}_key`]: '' });
      toast.success('Key removed');
      onSaved();
    } catch {
      toast.error('Remove failed');
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`ai-provider-${provider.key}`}>
      <div className="flex items-center gap-2">
        <h3 className="font-bold text-slate-900">{provider.label}</h3>
        <div className="flex-1" />
        {isSet ? (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5" />
            Connected
          </Badge>
        ) : status === 'fail' ? (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5" />
            Invalid key
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-slate-50 text-slate-600">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mr-1.5" />
            Not configured
          </Badge>
        )}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={e => { setValue(e.target.value); setStatus(null); }}
            placeholder={provider.placeholder}
            className="font-mono text-xs pr-9"
            data-testid={`ai-key-input-${provider.key}`}
          />
          <button
            onClick={() => setShow(s => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          >
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <Button variant="outline" size="sm" onClick={handleTest} disabled={!value || testing}>
          {testing ? '…' : 'Test'}
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!value || saving} data-testid={`ai-key-save-${provider.key}`}>
          {saving ? '…' : 'Save'}
        </Button>
        {isSet && (
          <Button variant="ghost" size="sm" onClick={handleRemove} className="text-red-600">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}


function MapKeyInput({ label, field, isSet, onSave }) {
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleSave = async () => {
    if (!value) return;
    setSaving(true);
    try {
      await onSave(value);
      setValue('');
      toast.success(`${label} saved`);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (field !== 'google_maps_key') return;
    setTesting(true);
    try {
      const body = value ? { key: value.trim() } : {};
      const res = await api.post('/routeops/settings/test-google-maps-key', body);
      const checks = res.data.checks || [];
      if (res.data.ok) {
        const lines = checks.length
          ? checks.map(c => `${c.ok ? '✅' : '❌'} ${c.api}: ${c.detail}`).join('\n')
          : 'Google Maps API key is valid';
        toast.success(lines + (value ? '\n\nYou can click Save now' : ''), { duration: 7000 });
      } else {
        const lines = checks.length
          ? checks.map(c => `${c.ok ? '✅' : '❌'} ${c.api}: ${c.detail}`).join('\n')
          : (res.data.error || 'Validation failed');
        toast.error(lines + (res.data.help ? `\n\n➜ ${res.data.help}` : ''), { duration: 12000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-lg p-3" style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{label}</span>
        {isSet ? (
          <Badge variant="outline" className="text-[10px]" style={{ color: 'var(--green)', borderColor: 'var(--green)' }}>Configured</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Not set</Badge>
        )}
        {field === 'google_maps_key' && (
          <Button
            variant={(isSet || value) ? 'default' : 'outline'}
            size="sm"
            onClick={handleTest}
            disabled={testing || (!isSet && !value)}
            className="text-[10px] ml-auto h-7 px-3 font-bold uppercase tracking-wide"
            style={(isSet || value) ? { background: 'var(--blue, #3b82f6)', color: '#fff' } : {}}
            data-testid="test-gmaps-key"
          >
            {testing ? 'Testing…' : (value ? 'Test Pasted Key' : (isSet ? 'Test Connection' : 'Paste or save key first'))}
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={isSet ? 'Key saved — enter new to replace' : 'Paste API key...'}
            className="font-mono text-xs pr-9"
            data-testid={`map-key-input-${field}`}
          />
          <button onClick={() => setShow(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <Button size="sm" onClick={handleSave} disabled={!value || saving} data-testid={`map-key-save-${field}`}
          style={{ background: 'var(--amber)', color: 'var(--bg-void)' }}>
          {saving ? '...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}


function QRDevicesSection() {
  const [qrData, setQrData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loadingQr, setLoadingQr] = useState(true);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const fetchData = async () => {
    try {
      setLoadingQr(true);
      const [qr, devs] = await Promise.all([
        api.get('/auth/qr-url'),
        api.get('/auth/my-devices'),
      ]);
      setQrData(qr.data);
      setDevices(devs.data || []);
    } catch {
      // ignore
    } finally {
      setLoadingQr(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const revokeDevice = async (tokenId) => {
    if (!window.confirm('Revoke this device? It will need to re-authenticate next time.')) return;
    try {
      await api.delete(`/auth/devices/${tokenId}`);
      toast.success('Device revoked');
      fetchData();
    } catch { toast.error('Revoke failed'); }
  };

  const qrUrl = qrData ? `${baseUrl}/qr-login?user=${qrData.user_id}&sig=${qrData.sig}` : '';

  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="text-sm font-bold uppercase tracking-wide mb-4" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)', letterSpacing: '0.08em' }}>
          <QrCode className="h-4 w-4 inline mr-2" style={{ color: 'var(--amber)' }} />
          Quick Login QR Code
        </h2>

        <div className="flex flex-col sm:flex-row gap-6">
          {/* QR Code */}
          <div className="flex flex-col items-center gap-3">
            {qrData ? (
              <div className="p-3 rounded-xl" style={{ background: '#ffffff' }}>
                <QRCodeSVG value={qrUrl} size={160} bgColor="#ffffff" fgColor="#0d1117" level="M" data-testid="settings-qr-code" />
              </div>
            ) : (
              <div className="w-[184px] h-[184px] rounded-xl flex items-center justify-center" style={{ background: 'var(--bg-raised)' }}>
                {loadingQr ? <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</span> : <QrCode className="h-8 w-8 opacity-30" />}
              </div>
            )}
            <p className="text-[11px] text-center max-w-[200px]" style={{ color: 'var(--text-muted)' }}>
              Scan this QR with your phone to log in instantly. First scan requires password, then it's passwordless.
            </p>
          </div>

          {/* Devices list */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="h-4 w-4" style={{ color: 'var(--text-muted)' }} />
              <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)' }}>
                Registered Devices ({devices.filter(d => d.is_active).length})
              </span>
            </div>
            {devices.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No devices registered yet. Scan the QR code to add one.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {devices.map(d => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between p-2.5 rounded-lg"
                    style={{ background: d.is_active ? 'var(--bg-raised)' : 'var(--bg-void)', opacity: d.is_active ? 1 : 0.5 }}
                    data-testid={`device-${d.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{d.device_label}</p>
                      <p className="text-[10px]" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fm)' }}>
                        Last used: {d.last_used_at ? new Date(d.last_used_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                      </p>
                    </div>
                    {d.is_active ? (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => revokeDevice(d.id)}
                        className="text-xs shrink-0 ml-2"
                        style={{ color: 'var(--red)' }}
                        data-testid={`device-revoke-${d.id}`}
                      >
                        <Trash className="h-3.5 w-3.5 mr-1" /> Revoke
                      </Button>
                    ) : (
                      <Badge variant="outline" className="text-[10px] shrink-0 ml-2" style={{ color: 'var(--text-muted)' }}>Revoked</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}



export default function Settings() {
  const ctx = useOutletContext() || {};
  const canEdit = ctx.canEditModule ? ctx.canEditModule('routeops_settings') : true;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dangerInput, setDangerInput] = useState('');

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/routeops/settings');
      setSettings(res.data);
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const updateField = (field, value) => setSettings(s => ({ ...s, [field]: value }));

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await api.put('/routeops/settings', {
        display_name: settings.display_name,
        role: settings.role,
      });
      toast.success('Profile saved');
    } finally { setSaving(false); }
  };

  const handleSavePlanner = async () => {
    try {
      await api.put('/routeops/settings', {
        week_start: settings.week_start,
        warning_threshold_pct: Number(settings.warning_threshold_pct),
        auto_plan_on_submit: settings.auto_plan_on_submit,
        postcode_sensitivity: Number(settings.postcode_sensitivity),
      });
      toast.success('Planner defaults saved');
    } catch { toast.error('Save failed'); }
  };

  const handleSaveDisplay = async () => {
    try {
      await api.put('/routeops/settings', {
        show_overweight_alerts: settings.show_overweight_alerts,
        show_unassigned_badge: settings.show_unassigned_badge,
        compact_mode: settings.compact_mode,
        date_format: settings.date_format,
        time_format: settings.time_format,
      });
      toast.success('Display settings saved');
    } catch { toast.error('Save failed'); }
  };

  const movePriority = (p, dir) => {
    const arr = [...(settings.ai_priority || [])];
    const i = arr.indexOf(p);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    updateField('ai_priority', arr);
    api.put('/routeops/settings', { ai_priority: arr });
  };

  const handleDangerClear = async (what, label) => {
    if (dangerInput !== 'CONFIRM') return toast.error('Type CONFIRM to proceed');
    try {
      await api.post(`/routeops/danger/clear/${what}`);
      toast.success(`${label} cleared`);
      setDangerInput('');
    } catch { toast.error('Failed'); }
  };

  if (loading || !settings) return <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</div>;

  const currentTheme = localStorage.getItem('fleet-theme') || 'dark';
  const setTheme = (theme) => {
    localStorage.setItem('fleet-theme', theme);
    if (theme === 'light') {
      document.documentElement.classList.add('theme-light');
    } else {
      document.documentElement.classList.remove('theme-light');
    }
    toast.success(`Switched to ${theme} theme`);
    // Force re-render
    window.location.reload();
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="routeops-settings-page">
      <div className="flex items-center gap-3 pb-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Settings</h1>
      </div>

      {!canEdit && <ReadOnlyBanner pageName="RouteOps Settings" />}

      <fieldset disabled={!canEdit} className="space-y-6 m-0 p-0 border-0 min-w-0" style={{ opacity: canEdit ? 1 : 0.85 }}>

      {/* Theme */}
      <Card>
        <CardContent className="p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)', letterSpacing: '0.08em' }}>Theme</h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setTheme('light')}
              className="flex items-center gap-3 p-4 rounded-lg border-2 transition-all"
              style={{
                background: currentTheme === 'light' ? 'var(--amber-glow)' : 'var(--bg-raised)',
                borderColor: currentTheme === 'light' ? 'var(--amber)' : 'var(--border-color)',
              }}
              data-testid="theme-light-btn"
            >
              <Sun className="h-5 w-5" style={{ color: currentTheme === 'light' ? 'var(--amber)' : 'var(--text-muted)' }} />
              <div className="text-left">
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Light</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Classic light theme</p>
              </div>
            </button>
            <button
              onClick={() => setTheme('dark')}
              className="flex items-center gap-3 p-4 rounded-lg border-2 transition-all"
              style={{
                background: currentTheme === 'dark' ? 'var(--amber-glow)' : 'var(--bg-raised)',
                borderColor: currentTheme === 'dark' ? 'var(--amber)' : 'var(--border-color)',
              }}
              data-testid="theme-dark-btn"
            >
              <Moon className="h-5 w-5" style={{ color: currentTheme === 'dark' ? 'var(--amber)' : 'var(--text-muted)' }} />
              <div className="text-left">
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Dark</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>RouteOps dark mode</p>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* QR Quick Login & Devices */}
      <QRDevicesSection />

      {/* Maps API Keys */}
      <Card>
        <CardContent className="p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)', letterSpacing: '0.08em' }}>Maps & Routing API Keys</h2>
          <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Required for the Live Route Planner. Keys are encrypted at rest.</p>
          <div className="space-y-3">
            <MapKeyInput
              label="Google Maps API Key"
              field="google_maps_key"
              isSet={settings.google_maps_key_set}
              onSave={(val) => updateField('google_maps_key', val)}
            />
            <MapKeyInput
              label="OpenRouteService Key (Fallback)"
              field="ors_key"
              isSet={settings.ors_key_set}
              onSave={(val) => updateField('ors_key', val)}
            />
          </div>
        </CardContent>
      </Card>


      {/* Profile */}
      <Card>
        <CardContent className="p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600 mb-3">User Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Display Name</Label>
              <Input value={settings.display_name || ''} onChange={e => updateField('display_name', e.target.value)} data-testid="setting-display-name" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <select
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
                value={settings.role || ''}
                onChange={e => updateField('role', e.target.value)}
              >
                <option value="">—</option>
                <option value="Manager">Manager</option>
                <option value="Dispatcher">Dispatcher</option>
                <option value="Viewer">Viewer</option>
              </select>
            </div>
          </div>
          <Button className="mt-3" onClick={handleSaveProfile} disabled={saving} data-testid="setting-save-profile">Save Profile</Button>
        </CardContent>
      </Card>

      {/* AI keys */}
      <Card>
        <CardContent className="p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600 mb-1">AI API Keys</h2>
          <p className="text-xs text-slate-500 mb-4">
            Keys are stored server-side per user. They're included in JSON backups. Never logged to audit.
          </p>
          <div className="space-y-3">
            {PROVIDERS.map(p => (
              <ProviderBlock key={p.key} provider={p} settings={settings} onSaved={fetchSettings} />
            ))}
          </div>
          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Priority Order</div>
            <p className="text-xs text-slate-500 mb-2">Top provider is used for Consensus synthesis and as default Single Response provider.</p>
            <div className="space-y-1">
              {(settings.ai_priority || []).map((p, i) => (
                <div key={p} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-sm">
                  <span className="font-mono text-slate-400 w-6">#{i + 1}</span>
                  <span className="flex-1 font-medium">{PROVIDERS.find(x => x.key === p)?.label || p}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => movePriority(p, -1)}>↑</Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => movePriority(p, 1)}>↓</Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Planner defaults */}
      <Card>
        <CardContent className="p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600 mb-3">Planner Defaults</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Week starts on</Label>
              <select
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
                value={settings.week_start}
                onChange={e => updateField('week_start', e.target.value)}
              >
                <option value="mon">Monday</option>
                <option value="sun">Sunday</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Amber warning threshold: {settings.warning_threshold_pct}%</Label>
              <input
                type="range" min="5" max="20"
                value={settings.warning_threshold_pct}
                onChange={e => updateField('warning_threshold_pct', e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <Label className="text-xs">Postcode grouping sensitivity</Label>
              <select
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
                value={settings.postcode_sensitivity}
                onChange={e => updateField('postcode_sensitivity', Number(e.target.value))}
              >
                <option value="2">First 2 chars</option>
                <option value="3">First 3 chars</option>
                <option value="4">First 4 chars</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Auto-plan on booking submit</Label>
              <Switch checked={!!settings.auto_plan_on_submit} onCheckedChange={v => updateField('auto_plan_on_submit', v)} />
            </div>
          </div>
          <Button className="mt-3" onClick={handleSavePlanner} data-testid="setting-save-planner">Save Planner Defaults</Button>
        </CardContent>
      </Card>

      {/* Display */}
      <Card>
        <CardContent className="p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600 mb-3">Notifications &amp; Display</h2>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show overweight alerts</Label>
              <Switch checked={!!settings.show_overweight_alerts} onCheckedChange={v => updateField('show_overweight_alerts', v)} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show unassigned count badge on planner icon</Label>
              <Switch checked={!!settings.show_unassigned_badge} onCheckedChange={v => updateField('show_unassigned_badge', v)} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Compact mode for planner grid</Label>
              <Switch checked={!!settings.compact_mode} onCheckedChange={v => updateField('compact_mode', v)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-xs">Date format</Label>
              <select
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
                value={settings.date_format}
                onChange={e => updateField('date_format', e.target.value)}
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Time format</Label>
              <select
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
                value={settings.time_format}
                onChange={e => updateField('time_format', e.target.value)}
              >
                <option value="24hr">24-hour</option>
                <option value="12hr">12-hour</option>
              </select>
            </div>
          </div>
          <Button className="mt-3" onClick={handleSaveDisplay}>Save Display</Button>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-red-200">
        <CardContent className="p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-red-600 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Danger Zone
          </h2>
          <p className="text-xs text-slate-600 mb-3">All actions are logged to the audit trail, which cannot be cleared.</p>
          <Label className="text-xs">Type <span className="font-mono font-bold">CONFIRM</span> to enable:</Label>
          <Input value={dangerInput} onChange={e => setDangerInput(e.target.value)} className="font-mono mb-3" data-testid="danger-confirm-input" />
          <div className="flex flex-wrap gap-2">
            <Button variant="destructive" disabled={dangerInput !== 'CONFIRM'} onClick={() => handleDangerClear('bookings', 'All bookings')}>
              Clear All Bookings
            </Button>
            <Button variant="destructive" disabled={dangerInput !== 'CONFIRM'} onClick={() => handleDangerClear('planner', 'Planner')}>
              Reset Planner
            </Button>
            <Button variant="destructive" disabled={dangerInput !== 'CONFIRM'} onClick={() => handleDangerClear('terminal', 'Terminal history')}>
              Clear Terminal History
            </Button>
          </div>
        </CardContent>
      </Card>
      </fieldset>
    </div>
  );
}
